<script setup lang="ts">
import { PhX } from '@phosphor-icons/vue';
import type { models } from 'cesg-shared';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FileViewer from '@/components/common/file-viewer.vue';

const props = defineProps<{
  file: File;
  title?: string;
}>();

const emit = defineEmits<{ close: [] }>();

const { t } = useI18n();

// Browser PDF viewers (via <embed>) already ship zoom, print, and search —
// reuse that instead of re-implementing a preview/zoom UI on top of a
// canvas renderer.
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const invoiceFile = ref<models.SupplierInvoiceFile | null>(null);

onMounted(async () => {
  invoiceFile.value = {
    fileId: props.file.name,
    name: props.file.name,
    blob: await fileToBase64(props.file),
  };
});
</script>

<template>
  <div class="modal is-active" role="dialog" aria-modal="true">
    <div class="modal-background" @click="emit('close')"></div>

    <div class="modal-card pdf-preview-modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ title ?? t('pdfMerger.previewTitle') }}
        </p>
        <button
          class="delete"
          type="button"
          :aria-label="t('cancel')"
          @click="emit('close')"
        ></button>
      </header>

      <section class="modal-card-body">
        <FileViewer v-if="invoiceFile" :files="[invoiceFile]" />
      </section>

      <footer class="modal-card-foot is-justify-content-flex-end">
        <button type="button" class="button" @click="emit('close')">
          <PhX :size="16" class="mr-1" />
          {{ t('cancel') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.pdf-preview-modal-card {
  width: 95vw;
  height: 95vh;
  max-width: none;
  max-height: none;
  display: flex;
  flex-direction: column;
}

.pdf-preview-modal-card .modal-card-body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  overflow: auto;
  background: #f5f5f5;
}

/* Bulma's .columns applies a negative inline margin to offset its
   .column children's own padding — that cancels out net-zero only as long
   as the enclosing container keeps its default padding. We zero the body's
   padding above, so the margin/padding pair must be zeroed here too, or the
   negative margin overflows the (now padding-less) body instead. */
.pdf-preview-modal-card .modal-card-body :deep(.file-viewer) {
  width: 100%;
  height: 100%;
  margin: 0;
}

/* file-viewer.vue's own column wrapper has no flex-grow, so it shrinks to
   the embed's content height (falling back to its hardcoded 800px "height"
   attribute) instead of filling the space this modal gives it — force it to
   stretch so the embed's height:100% below has something definite to
   resolve against. */
.pdf-preview-modal-card .modal-card-body :deep(.file-viewer > .column) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  padding: 0;
}

.pdf-preview-modal-card .modal-card-body :deep(embed) {
  width: 100%;
  height: 100%;
}

.pdf-preview-modal-card .modal-card-head,
.pdf-preview-modal-card .modal-card-foot {
  padding: 1.1rem 1.5rem;
}

.pdf-preview-modal-card .modal-card-head .delete {
  width: 2.25rem;
  height: 2.25rem;
  max-width: none;
  max-height: none;
}
</style>
